import {
    IConversation,
    IConversationCustomer,
    IWorkItem,
    IWorkItemConversation,
    IWorkItemSource,
    IWorkItemWithConversation,
} from '@dxp/core/models';
import { MediaType, UnknownCustomer, WorkItemState } from '@dxp/core/types';
import { IWorkItemChangeEvent } from './workitems.models';

export const transformConversation = (item: IConversation): IConversation => {
    return {
        conversationId: item.conversationId,
        channelType: item.channelType,
        queueId: item.queueId,
        queueName: item.queueName,
        queuedAt: new Date(item.queuedAt),
        dequeuedAt: item.dequeuedAt ? new Date(item.dequeuedAt) : null,
        workflowProperties: item.workflowProperties,
        fromNumber: item.fromNumber,
        dialedNumber: item.dialedNumber,
        callerIdName: item.callerIdName,
        position: item.position,
        enquiryCallState: item.enquiryCallState,
        callRecordingState: item.callRecordingState,
        outboundCallState: item.outboundCallState,
        outboundCallStateReason: item.outboundCallStateReason,
        serviceName: item.serviceName,
        voiceCallId: item.voiceCallId,
        monitoredBy: item.monitoredBy,
        isInternalCall: item.isInternalCall,
        customer: item.customer,
    };
};

export const transformConversations = (
    conversations: Record<string, IConversation>,
): IConversation[] => {
    return Object.values(conversations).map(c => transformConversation(c));
};

export const transformWorkItem = (item: IWorkItemSource): IWorkItem => {
    const conversations = transformConversations(item.conversations);

    return {
        workItemId: item.workItemId,
        conversations: conversations,
        businessUnitId: item.businessUnitId ?? null,
        customerId: item.customerId ?? null,
        workItemState: mapToWorkItemState(item.workItemState),
        lastStateChangeDate: new Date(item.lastStateChangeDate),
        createdAt: new Date(item.createdAt),
        primaryConversationId: item.primaryConversationId,
        usableClosureCodes: item.usableClosureCodes,
        channelId: item.channelId,
        channelIdentifier: item.channelIdentifier,
        primaryMediaType: item.primaryMediaType,
        primaryChannelType: item.primaryChannelType,
        remainingWrapUpResets: item.remainingWrapUpResets,
        wrapupDurationSeconds: item.wrapupDurationSeconds,
        forceCompletionCodeSelection: item.forceCompletionCodeSelection,
        isOutbound: item.isOutbound,
        customerIdentifier: item.customerIdentifier,
        isPatchedCall: item.isPatchedCall,
        isEnquiryCall: item.isEnquiryCall,
        serviceName: item.serviceName,
        voiceCallId: item.voiceCallId,
        enableChatEmojis: item.enableChatEmojis,
        displayCustomer: UnknownCustomer,
    };
};

export const transformWorkItemWithConversation = (
    item: IWorkItemWithConversation,
): IWorkItem => {
    const conversations = transformWorkItemConversations(item.conversations);
    const primary = item.conversations.find(
        c => c.id === item.primaryConversationId,
    );

    return {
        agentId: item.agentId,
        queueId: item.queueId,
        workItemId: item.id,
        conversations: conversations,
        businessUnitId: item.businessUnitId ?? null,
        customerId: primary?.customer?.id ?? null,
        workItemState: mapToWorkItemState(item.status),
        lastStateChangeDate: new Date(item.createdAt),
        createdAt: new Date(item.createdAt),
        primaryConversationId: item.primaryConversationId,
        usableClosureCodes: [],
        channelId: 0,
        primaryMediaType: item.mediaType,
        primaryChannelType: item.channelType,
        remainingWrapUpResets: 0,
        wrapupDurationSeconds: 0,
        customerIdentifier: primary?.customer?.customerIdentifier,
        primaryConversationCustomer: primary?.customer,
        enableChatEmojis: false,
        displayCustomer: calculateDisplayCustomer(
            item.mediaType,
            undefined,
            primary?.customer,
        ),
    };
};

export const mapToWorkItemState = (status: string): WorkItemState => {
    switch (status) {
        case 'Active':
            return 'Connected';
        case 'LookUp':
            return 'LookUp';
        case 'Preview':
            return 'Preview';
        case 'Connected':
            return 'Connected';
        case 'Afterwork':
            return 'Afterwork';
        case 'AfterWork':
            return 'Afterwork';
        default:
            return 'Closed';
    }
};

export const transformWorkItemConversation = (
    item: IWorkItemConversation,
): IConversation => {
    return {
        conversationId: item.id,
        channelType: item.channelType,
        queuedAt: new Date(item.startedAt),
        dequeuedAt: new Date(item.agentJoinedAt),
        queueId: '',
        queueName: '',
        workflowProperties: {},
        fromNumber: '',
        dialedNumber: '',
        callerIdName: '',
        position: 0,
        enquiryCallState: '',
        callRecordingState: '',
        outboundCallState: '',
        outboundCallStateReason: '',
        customer: item.customer,
    };
};

export const transformWorkItemConversations = (
    conversations: IWorkItemConversation[],
): IConversation[] => {
    return conversations.map(c => transformWorkItemConversation(c));
};

export const calculateDisplayCustomer = (
    mediaType: MediaType,
    identifier?: string,
    customer?: IConversationCustomer,
): string => {
    const {
        firstName = '',
        middleName = '',
        lastName = '',
        email = '',
        phoneNumber = '',
        customerIdentifier = '',
    } = customer || {};

    const fullName = [firstName, middleName, lastName]
        .map(n => n.trim())
        .filter(Boolean)
        .join(' ');

    switch (mediaType) {
        case 'Webchat':
        case 'Messaging': {
            return (
                fullName ||
                email?.trim().toLowerCase() ||
                phoneNumber?.trim().toLowerCase() ||
                identifier?.toLowerCase() ||
                customerIdentifier.toLowerCase() ||
                UnknownCustomer
            );
        }
        default: {
            return (
                fullName ||
                identifier?.toLowerCase() ||
                customerIdentifier.toLowerCase() ||
                UnknownCustomer
            );
        }
    }
};

export const hasCustomerChanged = (
    prev?: IConversationCustomer,
    next?: IConversationCustomer,
): boolean => {
    if (!prev && !next) return false;

    if (!prev || !next) return true;

    return (
        prev.id !== next.id ||
        prev.firstName !== next.firstName ||
        prev.middleName !== next.middleName ||
        prev.lastName !== next.lastName ||
        prev.customerIdentifier !== next.customerIdentifier ||
        prev.email !== next.email ||
        prev.phoneNumber !== next.phoneNumber
    );
};

export const mergeEnrichedCustomers = (
    prev: IWorkItem,
    next: IWorkItem,
): IWorkItem => {
    const prevConvs = prev.conversations ?? [];
    const nextConvs = next.conversations ?? [];
    const prevByConvId = new Map(prevConvs.map(c => [c.conversationId, c]));

    const mergedConvs = nextConvs.map(c => {
        const prevConv = prevByConvId.get(c.conversationId);
        const preservedCustomer = prevConv?.customer;

        if (preservedCustomer && c.customer == null) {
            return { ...c, customer: preservedCustomer };
        }

        return c;
    });

    const primaryIdChanged =
        prev.primaryConversationId !== next.primaryConversationId;

    const preservedPrimaryCustomer = primaryIdChanged
        ? undefined
        : (prev.primaryConversationCustomer ??
          prevConvs.find(c => c.conversationId === prev.primaryConversationId)
              ?.customer);

    const derivedPrimaryCustomer = mergedConvs.find(
        c => c.conversationId === next.primaryConversationId,
    )?.customer;

    const nextPrimaryCustomer =
        derivedPrimaryCustomer ?? preservedPrimaryCustomer;

    const nextDisplayCustomer = calculateDisplayCustomer(
        next.primaryMediaType,
        next.customerIdentifier,
        nextPrimaryCustomer,
    );

    return {
        ...next,
        conversations: mergedConvs,
        primaryConversationCustomer: nextPrimaryCustomer,
        displayCustomer: nextDisplayCustomer,
    };
};

export const hasWorkItemChanged = (
    prev: IWorkItem,
    next: IWorkItem,
): boolean => {
    const prevLast = prev.lastStateChangeDate
        ? prev.lastStateChangeDate.getTime()
        : 0;
    const nextLast = next.lastStateChangeDate
        ? next.lastStateChangeDate.getTime()
        : 0;

    if (prevLast !== nextLast) return true;
    if (prev.workItemState !== next.workItemState) return true;
    if (prev.primaryConversationId !== next.primaryConversationId) return true;
    if (prev.remainingWrapUpResets !== next.remainingWrapUpResets) return true;
    if (prev.displayCustomer !== next.displayCustomer) return true;
    if (prev.customerId !== next.customerId) return true;
    if (prev.channelIdentifier !== next.channelIdentifier) return true;
    if (prev.enableChatEmojis !== next.enableChatEmojis) return true;

    const prevConvs = prev.conversations ?? [];
    const nextConvs = next.conversations ?? [];

    if (prevConvs.length !== nextConvs.length) return true;

    const prevById = new Map(prevConvs.map(c => [c.conversationId, c]));

    for (const b of nextConvs) {
        const a = prevById.get(b.conversationId);
        if (!a) return true;

        if (a.queueId !== b.queueId) return true;
        if (a.position !== b.position) return true;
        if (a.outboundCallState !== b.outboundCallState) return true;
        if (a.outboundCallStateReason !== b.outboundCallStateReason)
            return true;
        if (a.enquiryCallState !== b.enquiryCallState) return true;
        if (a.monitoredBy !== b.monitoredBy) return true;
        if (a.callRecordingState !== b.callRecordingState) return true;

        if (a.queuedAt?.getTime() !== b.queuedAt?.getTime()) return true;
        if (a.dequeuedAt?.getTime() !== b.dequeuedAt?.getTime()) return true;

        if (hasCustomerChanged(a.customer, b.customer)) return true;
    }

    return false;
};

export const mapByWorkItemId = (
    workItems: IWorkItem[],
): Map<string, IWorkItem> => {
    const map = new Map<string, IWorkItem>();
    for (const wi of workItems) {
        if (wi.workItemId) map.set(wi.workItemId, wi);
    }
    return map;
};

export const upsertWorkItems = (
    existing: IWorkItem[],
    incoming: IWorkItem[],
    prune = false,
): { next: IWorkItem[]; changed: boolean } => {
    const next = existing.slice();
    const byId = new Map<string, IWorkItem>();
    const indexById = new Map<string, number>();

    for (let i = 0; i < existing.length; i++) {
        const wi = existing[i];
        const id = wi.workItemId;

        byId.set(id, wi);
        indexById.set(id, i);
    }

    const incomingIds = prune ? new Set<string>() : null;
    let changed = false;

    for (const item of incoming) {
        const id = item.workItemId;
        const prev = byId.get(id);

        if (incomingIds) incomingIds.add(id);

        if (!prev) {
            indexById.set(id, next.length);
            byId.set(id, item);
            next.push(item);
            changed = true;
            continue;
        }

        if (!hasWorkItemChanged(prev, item)) continue;

        const idx = indexById.get(id);

        if (idx == null || idx < 0 || idx >= next.length) {
            indexById.set(id, next.length);
            byId.set(id, item);
            next.push(item);
        } else {
            next[idx] = item;
            byId.set(id, item);
        }

        changed = true;
    }

    if (incomingIds && prune) {
        const pruned = next.filter(wi => incomingIds.has(wi.workItemId));

        if (pruned.length !== next.length) {
            changed = true;
        }

        return { next: pruned, changed };
    }

    return { next, changed };
};

export const diffWorkItems = (
    prev: IWorkItem[],
    next: IWorkItem[],
): IWorkItemChangeEvent[] => {
    const prevById = mapByWorkItemId(prev);
    const nextById = mapByWorkItemId(next);

    const events: IWorkItemChangeEvent[] = [];

    for (const [id, current] of nextById) {
        const previous = prevById.get(id);

        if (!previous) {
            events.push({ type: 'added', id, workitem: current });
            continue;
        }

        if (previous.workItemState !== current.workItemState) {
            events.push({
                type: 'state',
                id,
                workitem: current,
            });
        }

        if (previous.displayCustomer !== current.displayCustomer) {
            events.push({
                type: 'customer',
                id,
                workitem: current,
            });
        }
    }

    for (const [id, previous] of prevById) {
        if (!nextById.has(id)) {
            events.push({ type: 'removed', id, workitem: previous });
        }
    }

    return events;
};
